// src/pages/employees/EmployeeProfile.jsx

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft, Mail, Phone, MapPin, Briefcase, Calendar, DollarSign, Package, History, FileText, Upload, Download, Trash2, X, Eye } from 'lucide-react';
import { employeeService } from '../../services/employeeService';
import { api } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import StatusBadge from '../../components/StatusBadge';

const EmployeeProfile = () => {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user && ['super_admin', 'hr_manager'].includes(user.role);
  const [employee, setEmployee] = useState(null);
  const [salaryStructure, setSalaryStructure] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadEmployee = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await employeeService.getEmployee(employeeId);
        setEmployee(res.data || res);
      } catch (err) {
        setError(err.message || 'Failed to load employee');
      } finally {
        setLoading(false);
      }
    };

    const loadSalaryStructure = async () => {
      try {
        const res = await api.get('/api/payroll/salary-structure/' + employeeId);
        setSalaryStructure(res.data || res);
      } catch (err) {
        // No salary structure saved for this employee yet - keep null, cards show 0.
        setSalaryStructure(null);
      }
    };

    loadEmployee();
    loadSalaryStructure();
  }, [employeeId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !employee) {
    return (
      <div>
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-primary-light hover:text-primary mb-6 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <div className="bg-white rounded-card shadow-soft border border-gray-100 p-8 text-center">
          <p className="text-red-600">{error || 'Employee not found'}</p>
        </div>
      </div>
    );
  }

  const salary = { basic: salaryStructure?.basic_salary || 0 };
  const allowances = salaryStructure?.allowances || {};
  const deductions = salaryStructure?.deductions || {};
  const gross = (salary.basic || 0) + (allowances.housing || 0) + (allowances.transport || 0) + (allowances.medical || 0) + (allowances.other || 0);
  const totalDeductions = (deductions.tax || 0) + (deductions.other || 0);
  const netPay = gross - totalDeductions;

  return (
    <div>
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-primary-light hover:text-primary mb-6 text-sm"
      >
        <ArrowLeft className="w-4 h-4" />
        Back
      </button>

      {/* Header Card */}
      <div className="bg-white rounded-card shadow-soft border border-gray-100 mb-6">
        <div className="p-6">
          <div className="flex items-start gap-6 flex-wrap">
            {employee.photo && (
              <div className="w-24 h-24 rounded-control overflow-hidden bg-gray-100 flex-shrink-0">
                <img src={employee.photo} alt={employee.full_name} className="w-full h-full object-cover" />
              </div>
            )}
            <div className="flex-1">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <h1 className="text-2xl font-semibold text-gray-900">{employee.full_name}</h1>
                  <p className="text-sm text-gray-500 mt-1">{employee.employee_id}</p>
                  <div className="flex items-center gap-3 mt-3 flex-wrap">
                    <StatusBadge status={employee.status} />
                    <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full capitalize">
                      {(employee.role || '').replace(/_/g, ' ')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact & Personal Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Contact Card */}
        <div className="bg-white rounded-card shadow-soft border border-gray-100 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Contact Information</h2>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Mail className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500">Work Email</p>
                <p className="text-sm text-gray-800 break-all">{employee.email}</p>
              </div>
            </div>
            {employee.personal_email && (
              <div className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500">Personal Email</p>
                  <p className="text-sm text-gray-800 break-all">{employee.personal_email}</p>
                </div>
              </div>
            )}
            <div className="flex items-start gap-3">
              <Phone className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-xs text-gray-500">Phone</p>
                <p className="text-sm text-gray-800">{employee.phone}</p>
              </div>
            </div>
            {employee.cnic && (
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs text-gray-500">CNIC</p>
                  <p className="text-sm text-gray-800">{employee.cnic}</p>
                </div>
              </div>
            )}
             {employee.father_name && (
              <div className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs text-gray-500">Father Name</p>
                  <p className="text-sm text-gray-800">{employee.father_name}</p>
                </div>
              </div>
            )}
            {employee.address && (
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs text-gray-500">Address</p>
                  <p className="text-sm text-gray-800">{employee.address}</p>
                </div>
              </div>
            )}
               {employee.bank_name && (
              <div className="flex items-start gap-3">
                <DollarSign className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs text-gray-500">Bank Account</p>
                  <p className="text-sm text-gray-800">{employee.bank_name} — {employee.bank_account_number}</p>
                </div>
              </div>
            )}

            {employee.emergency_contact_name && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-xs font-medium text-gray-600 mb-2">Emergency Contact</p>
                <p className="text-sm text-gray-800">{employee.emergency_contact_name}</p>
                {employee.emergency_contact_phone && (
                  <p className="text-sm text-gray-600">{employee.emergency_contact_phone}</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Job Info Card */}
        <div className="bg-white rounded-card shadow-soft border border-gray-100 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Job Information</h2>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Briefcase className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-xs text-gray-500">Department</p>
                <p className="text-sm text-gray-800">{employee.department}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Briefcase className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-xs text-gray-500">Designation</p>
                <p className="text-sm text-gray-800">{employee.designation}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Calendar className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-xs text-gray-500">Date of Joining</p>
                <p className="text-sm text-gray-800">
                  {employee.joining_date ? new Date(employee.joining_date).toLocaleDateString() : 'N/A'}
                </p>
              </div>
            </div>
            {employee.date_of_birth && (
              <div className="flex items-start gap-3">
                <Calendar className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs text-gray-500">Date of Birth</p>
                  <p className="text-sm text-gray-800">
                    {new Date(employee.date_of_birth).toLocaleDateString()}
                  </p>
                </div>
              </div>
            )}
            {employee.reporting_manager && (
              <div className="flex items-start gap-3">
                <Briefcase className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs text-gray-500">Reporting Manager</p>
                  <p className="text-sm text-gray-800">{employee.reporting_manager}</p>
                </div>
              </div>
            )}
            <div className="flex items-start gap-3 mt-4 pt-4 border-t border-gray-100">
              <span className="text-xs text-gray-500">Employment Type</span>
              <span className="text-sm text-gray-800 capitalize">{employee.employment_type}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Salary Card */}
      <div className="bg-white rounded-card shadow-soft border border-gray-100 p-6 mb-6">
        <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <DollarSign className="w-4 h-4" />
          Salary & Compensation
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-50 rounded-control p-4">
            <p className="text-xs text-gray-500 mb-1">Basic Salary</p>
            <p className="text-sm font-semibold text-gray-800">PKR {(salary.basic || 0).toLocaleString()}</p>
          </div>
          <div className="bg-gray-50 rounded-control p-4">
            <p className="text-xs text-gray-500 mb-1">Gross Pay</p>
            <p className="text-sm font-semibold text-gray-800">PKR {gross.toLocaleString()}</p>
          </div>
          <div className="bg-gray-50 rounded-control p-4">
            <p className="text-xs text-gray-500 mb-1">Total Deductions</p>
            <p className="text-sm font-semibold text-red-600">PKR {totalDeductions.toLocaleString()}</p>
          </div>
          <div className="bg-green-50 rounded-control p-4">
            <p className="text-xs text-gray-500 mb-1">Net Pay</p>
            <p className="text-sm font-semibold text-green-600">PKR {netPay.toLocaleString()}</p>
          </div>
        </div>

        {/* Allowances & Deductions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-xs font-medium text-gray-600 mb-3">Allowances</h3>
            <div className="space-y-2 text-sm">
              {allowances.housing > 0 && (
                <div className="flex justify-between text-gray-700">
                  <span>Housing</span>
                  <span className="font-medium">PKR {allowances.housing.toLocaleString()}</span>
                </div>
              )}
              {allowances.transport > 0 && (
                <div className="flex justify-between text-gray-700">
                  <span>Transport</span>
                  <span className="font-medium">PKR {allowances.transport.toLocaleString()}</span>
                </div>
              )}
              {allowances.medical > 0 && (
                <div className="flex justify-between text-gray-700">
                  <span>Medical</span>
                  <span className="font-medium">PKR {allowances.medical.toLocaleString()}</span>
                </div>
              )}
              {allowances.other > 0 && (
                <div className="flex justify-between text-gray-700">
                  <span>Other</span>
                  <span className="font-medium">PKR {allowances.other.toLocaleString()}</span>
                </div>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-xs font-medium text-gray-600 mb-3">Deductions</h3>
            <div className="space-y-2 text-sm">
              {deductions.tax > 0 && (
                <div className="flex justify-between text-gray-700">
                  <span>Tax</span>
                  <span className="font-medium">PKR {deductions.tax.toLocaleString()}</span>
                </div>
              )}
              {deductions.other > 0 && (
                <div className="flex justify-between text-gray-700">
                  <span>Other</span>
                  <span className="font-medium">PKR {deductions.other.toLocaleString()}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Assets Card */}
      {employee.assigned_assets && employee.assigned_assets.length > 0 && (
        <div className="bg-white rounded-card shadow-soft border border-gray-100 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Package className="w-4 h-4" />
            Assigned Assets
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {employee.assigned_assets.map((asset, idx) => (
              <div key={idx} className="flex items-center gap-2 p-3 bg-gray-50 rounded-control text-sm">
                <Package className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <span className="text-gray-700">{asset}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Employment Lifecycle History (Join / Offboard / Rejoin cycles) */}
      {((employee.rejoin_cycle_history && employee.rejoin_cycle_history.length > 0) ||
        (employee.lifecycle_history && employee.lifecycle_history.length > 0)) && (
        <div className="bg-white rounded-card shadow-soft border border-gray-100 p-6 mt-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <History className="w-4 h-4" />
            Employment Lifecycle History
          </h2>

          {employee.rejoin_cycle_history && employee.rejoin_cycle_history.length > 0 ? (
            <div className="space-y-3">
              <p className="text-xs text-gray-400">Most recent 10 join / offboard / rejoin cycles.</p>
              {[...employee.rejoin_cycle_history].reverse().map((cycle, idx) => (
                <div key={idx} className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-4 bg-gray-50 rounded-control border border-gray-100">
                  <div>
                    <p className="text-[10px] text-gray-400 uppercase font-semibold">Previous Join Date</p>
                    <p className="text-sm text-gray-800 mt-0.5">{cycle.old_join_date || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400 uppercase font-semibold">Offboard Date</p>
                    <p className="text-sm text-gray-800 mt-0.5">{cycle.offboard_date || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400 uppercase font-semibold">Rejoin Date</p>
                    <p className="text-sm text-primary font-medium mt-0.5">{cycle.rejoin_date || 'N/A'}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {[...employee.lifecycle_history].reverse().map((entry, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-control text-sm">
                  <span className={'font-medium ' + (entry.event === 'Rejoined' ? 'text-green-600' : 'text-gray-700')}>{entry.event}</span>
                  <span className="text-gray-500">{entry.date}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Employee Document Vault (PDF/Image uploads, Admin/HR only) */}
      {isAdmin && <DocumentVaultCard employeeId={employeeId} />}
    </div>
  );
};

// ===== Employee Document Vault =====
const DocumentVaultCard = ({ employeeId }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [description, setDescription] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileType, setFileType] = useState('pdf');
  const [deletingId, setDeletingId] = useState('');

  const loadDocuments = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await employeeService.getEmployeeDocuments(employeeId);
      setDocuments(res.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    setFileType(file.type === 'application/pdf' ? 'pdf' : 'image');
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setUploadError('');
    if (!selectedFile) {
      setUploadError('Please choose a PDF or image file.');
      return;
    }
    setUploading(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(selectedFile);
      });
      await employeeService.uploadEmployeeDocument(employeeId, {
        filename: selectedFile.name,
        file_type: fileType,
        file_base64: base64,
        description: description || null
      });
      setShowUpload(false);
      setSelectedFile(null);
      setDescription('');
      loadDocuments();
    } catch (err) {
      setUploadError(err.message || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

const handlePreview = (doc) => {
    const url = employeeService.getPreviewUrl(employeeId, doc.id);
    window.open(url, '_blank');
  };
  const handleDownload = async (doc) => {
    try {
      await employeeService.downloadEmployeeDocument(employeeId, doc.id, doc.filename);
    } catch (err) {
      setError(err.message || 'Failed to download document');
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Delete this document? This cannot be undone.')) return;
    setDeletingId(docId);
    try {
      await employeeService.deleteEmployeeDocument(employeeId, docId);
      loadDocuments();
    } catch (err) {
      setError(err.message || 'Failed to delete document');
    } finally {
      setDeletingId('');
    }
  };

  return (
    <div className="bg-white rounded-card shadow-soft border border-gray-100 p-6 mt-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
          <FileText className="w-4 h-4" />
          Document Vault
        </h2>
        <button
          onClick={() => setShowUpload(true)}
          className="flex items-center gap-1.5 bg-primary hover:bg-primary-dark text-white px-3 py-2 rounded-control text-xs font-medium transition-colors"
        >
          <Upload className="w-3.5 h-3.5" />
          Upload File
        </button>
      </div>

      {error && <div className="mb-4 px-4 py-2.5 rounded-control bg-red-50 text-red-700 text-sm">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-8 text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : documents.length === 0 ? (
        <p className="text-sm text-gray-400 py-4">No documents uploaded yet.</p>
      ) : (
        <div className="space-y-2">
          {documents.map((doc) => (
            <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-control border border-gray-100">
              <div
                className="flex items-center gap-3 min-w-0 cursor-pointer group"
                onClick={() => handlePreview(doc)}
                title="Click to preview"
              >
                <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm text-gray-800 truncate group-hover:text-primary group-hover:underline">{doc.filename}</p>
                  <p className="text-xs text-gray-400">
                    {doc.file_type.toUpperCase()} &bull; Uploaded by {doc.uploaded_by_name || 'Unknown'} &bull; {new Date(doc.uploaded_at).toLocaleDateString()}
                    {doc.description ? (' — ' + doc.description) : ''}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={() => handlePreview(doc)} className="p-2 text-gray-400 hover:text-primary" title="Preview">
                  <Eye className="w-4 h-4" />
                </button>
                <button onClick={() => handleDownload(doc)} className="p-2 text-gray-400 hover:text-primary" title="Download">
                  <Download className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(doc.id)}
                  disabled={deletingId === doc.id}
                  className="p-2 text-gray-400 hover:text-red-500 disabled:opacity-50"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showUpload && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-card shadow-elevated w-full max-w-md">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Upload Document</h2>
              <button onClick={() => setShowUpload(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleUpload} className="p-5 space-y-4">
              {uploadError && <div className="px-4 py-2.5 rounded-control bg-red-50 text-red-700 text-sm">{uploadError}</div>}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">File (PDF or Image) *</label>
                <input
                  type="file"
                  accept="application/pdf,image/*"
                  onChange={handleFileChange}
                  className="w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-3 file:rounded-control file:border-0 file:text-xs file:font-medium file:bg-primary/10 file:text-primary hover:file:bg-primary/20"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Description (optional)</label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="e.g. Signed contract, CNIC copy"
                  className="w-full px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light"
                />
              </div>
              <div className="flex items-center justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowUpload(false)}
                  className="px-4 py-2.5 rounded-control text-sm font-medium text-gray-600 hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={uploading}
                  className="flex items-center gap-1.5 bg-primary hover:bg-primary-dark text-white px-4 py-2.5 rounded-control text-sm font-medium disabled:opacity-60">
                  {uploading && <Loader2 className="w-4 h-4 animate-spin" />}
                  Upload
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeProfile;